import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useTheme } from "@mui/material";
import { useThemeContext } from "../context/ThemeContext";
import athrvLogoWhite from "../img/Athrv-white.png";
import awsLogoWhite from "../img/aws-1.png";
import awsLogoDark from "../img/aws-main.png";
import "../styles/sign-up.css";

const CustomTextField = ({ 
  label, 
  type = "text", 
  field, 
  value, 
  onChange, 
  onBlur, 
  error,
  touched,
  placeholder,
  isLargeScreen,
  showPasswordToggle = false
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const hasError = touched && error;
  
  const inputType = showPasswordToggle ? (showPassword ? "text" : "password") : type;

  return (
    <div className={`text-field-container ${isLargeScreen ? 'large' : ''}`}>
      <div className="input-wrapper">
        <input
          type={inputType}
          value={value}
          placeholder={placeholder || label}
          onChange={(e) => onChange(field, e.target.value)}
          onBlur={() => onBlur(field)}
          className={`text-field-input ${hasError ? 'error' : ''} ${isLargeScreen ? 'large' : ''} ${showPasswordToggle ? 'with-icon' : ''}`}
        />
        {showPasswordToggle && (
          <button
            type="button"
            className={`password-toggle ${isLargeScreen ? 'large' : ''}`}
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <Eye size={isLargeScreen ? 22 : 18} /> : <EyeOff size={isLargeScreen ? 22 : 18} />}
          </button>
        )}
      </div>
      {hasError && (
        <p className={`text-field-error ${isLargeScreen ? 'large' : ''}`}>
          {error}
        </p>
      )}
    </div>
  );
};

const Shield = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

const Gauge = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="m14.31 8 5.74 9.94M9.69 8h11.48M7.38 12l5.74-9.94M9.69 16 3.95 6.06M14.31 16H2.83m13.79-4-5.74 9.94"></path>
  </svg>
);

const Settings = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3v3m6.366-.366-2.12 2.12M21 12h-3m.366 6.366-2.12-2.12M12 21v-3m-6.366.366 2.12-2.12M3 12h3m-.366-6.366 2.12 2.12"></path>
  </svg>
);

const Sun = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="5"></circle>
    <line x1="12" y1="1" x2="12" y2="3"></line>
    <line x1="12" y1="21" x2="12" y2="23"></line>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
    <line x1="1" y1="12" x2="3" y2="12"></line>
    <line x1="21" y1="12" x2="23" y2="12"></line>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
  </svg>
);

const Moon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
  </svg>
);

const Eye = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOff = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

const DevOps = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.6,6.62C21.58,6.62 24,9 24,12C24,14.96 21.58,17.37 18.6,17.37C17.15,17.37 15.8,16.81 14.78,15.8L12,13.34L9.17,15.85C8.2,16.82 6.84,17.38 5.4,17.38C2.42,17.38 0,14.96 0,12C0,9.04 2.42,6.62 5.4,6.62C6.84,6.62 8.2,7.18 9.17,8.15L12,10.66L14.83,8.2C15.8,7.18 17.15,6.62 18.6,6.62M7.8,14.39L10.5,12L7.84,9.65C7.16,8.97 6.31,8.62 5.4,8.62C3.53,8.62 2,10.13 2,12C2,13.87 3.53,15.38 5.4,15.38C6.31,15.38 7.16,15.03 7.8,14.39M16.2,9.61L13.5,12L16.16,14.35C16.84,15.03 17.7,15.37 18.6,15.37C20.47,15.37 22,13.87 22,12C22,10.13 20.47,8.62 18.6,8.62C17.7,8.62 16.84,8.97 16.2,9.61Z" />
  </svg>
);

const FeatureCard = ({ icon: Icon, title, description, isLargeScreen }) => (
  <div className={`feature-card ${isLargeScreen ? 'large' : ''}`}>
    <div className={`feature-icon ${isLargeScreen ? 'large' : ''}`}>
      <Icon size={isLargeScreen ? 36 : 26} />
    </div>
    <div>
      <h3 className={`feature-title ${isLargeScreen ? 'large' : ''}`}>
        {title}
      </h3>
      <p className={`feature-description ${isLargeScreen ? 'large' : ''}`}>
        {description}
      </p>
    </div>
  </div>
);

const Snackbar = ({ open, message, severity, onClose }) => {
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        onClose();
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [open, onClose]);

  if (!open) return null;

  const bgColor = severity === 'success' ? '#4caf50' : '#f44336';

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: bgColor,
      color: '#fff',
      padding: '12px 24px',
      borderRadius: '4px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 10000,
      fontSize: '14px',
      maxWidth: '500px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    }}>
      <span>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
          fontSize: '18px',
          padding: '0',
          lineHeight: '1',
        }}
      >
        ×
      </button>
    </div>
  );
};

const signupValidationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required")
    .max(254)
    .test("includes-dot", "Email must contain at least one dot (.)", (val) =>
      val ? val.includes(".") : false
    ),
  password: Yup.string()
    .required("Password is required")
    .min(6)
    .max(128)
    .matches(/[a-z]/, "Must contain at least one lowercase")
    .matches(/[A-Z]/, "Must contain at least one uppercase")
    .matches(/[0-9]/, "Must contain at least one number")
    .matches(/[^a-zA-Z0-9\s]/, "Must contain exactly one special character")
    .test(
      "exact-special-char",
      "Must contain exactly one special character",
      (value) => {
        if (!value) return false;
        const special = value.replace(/[a-zA-Z0-9]/g, "");
        return special.length === 1;
      }
    ),
  user_phone: Yup.string()
    .required("Phone number is required")
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits"),
  organization_name: Yup.string().when("userType", {
    is: "organization",
    then: Yup.string().required("Organization name is required"),
  }),
  contact_email: Yup.string().when("userType", {
    is: "organization",
    then: Yup.string()
      .email("Enter a valid contact email")
      .required("Contact email is required")
      .max(254)
      .test("includes-dot", "Must contain at least one dot (.)", (val) =>
        val ? val.includes(".") : false
      ),
  }),
  phone: Yup.string().when("userType", {
    is: "organization",
    then: Yup.string()
      .required("Organization phone is required")
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits"),
  }),
  address: Yup.string(),
});

const SignUpPage = () => {
  const theme = useTheme();
  const { toggleTheme } = useThemeContext();
  const isDarkMode = theme.palette.mode === "dark";
  
  const [userType, setUserType] = useState("individual");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [savedEmail, setSavedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', content: '' });
  const [termsData, setTermsData] = useState(null);
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchTermsAndConditions();
  }, []);

  const fetchTermsAndConditions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/terms-and-condtions`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.length > 0) {
          setTermsData(data.data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching terms:', error);
    }
  };

  const handleShowTerms = () => {
    if (termsData) {
      setDialogContent({
        title: 'Terms and Conditions',
        content: termsData.terms_and_condition_content || 'No terms available'
      });
      setShowTermsDialog(true);
    }
  };

  const handleShowPrivacy = () => {
    if (termsData) {
      setDialogContent({
        title: 'Privacy Policies',
        content: termsData.privacy_policy_content || 'No privacy policy available'
      });
      setShowTermsDialog(true);
    }
  };

  const handleCloseTermsDialog = () => {
    setShowTermsDialog(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isLargeScreen = windowWidth >= 1500;

  const handleSendOtp = async (values) => {
    const {
      firstName,
      lastName,
      email,
      password,
      user_phone,
      organization_name,
      address,
      contact_email,
      phone,
    } = values;

    const requestData = {
      fname: firstName,
      lname: lastName,
      email: email,
      password: password,
      user_phone: user_phone,
      organization_name: userType === "individual" ? `${firstName} ${lastName}` : organization_name,
      organization_type: userType === "individual" ? "I" : "O",
      address: address,
      contact_email: userType === "individual" ? email : contact_email,
      phone: userType === "individual" ? user_phone : phone,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/auth/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        }
      );

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        setSnackbarMessage("Server error. Please try again later.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        throw new Error("Server error");
      }

      if (!response.ok) {
        let errorMsg = "Registration failed. Please try again.";
        if (data.error) errorMsg = data.error;
        else if (data.msg) errorMsg = data.msg;
        else if (data.message) errorMsg = data.message;
        else if (data.detail) errorMsg = data.detail;

        setSnackbarMessage(errorMsg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        throw new Error(errorMsg);
      }

      const successMsg = data.msg || data.message || "OTP sent successfully! Please check your email.";
      setSnackbarMessage(successMsg);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsOtpSent(true);
      setSavedEmail(email);
      return true;
    } catch (err) {
      if (!snackbarOpen) {
        let errorMsg = "Registration failed. Please try again.";
        if (err.message && err.message.includes("Failed to fetch")) {
          errorMsg = "Unable to connect to server. Please check your internet connection.";
        } else if (err.message) {
          errorMsg = err.message;
        }
        setSnackbarMessage(errorMsg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
      throw err;
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/auth/verify-otp?email=${savedEmail}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ otp }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        const errorMsg = data.msg || "OTP verification failed. Please check your OTP and try again.";
        setSnackbarMessage(errorMsg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(data.msg || "Account verified successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    } catch (err) {
      setSnackbarMessage("Network error. Please try again later.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    const formSection = document.querySelector('.form-section');
    if (!formSection) return;

    let scrollTimeout;
    
    const handleScroll = () => {
      formSection.classList.add('scrolling');
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        formSection.classList.remove('scrolling');
      }, 1000);
    };

    formSection.addEventListener('scroll', handleScroll);
    
    return () => {
      formSection.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  return (
    <div className={`signup-container ${isMobile ? 'mobile' : ''} ${isTablet ? 'tablet' : ''} ${isLargeScreen ? 'large' : ''}`}>
      <div className="overlay" />

      <div className={`main-card ${isMobile || isTablet ? 'stacked' : ''} ${isLargeScreen ? 'large' : ''}`}>
        {!isMobile && !isTablet && (
        <div className={`features-section ${isTablet ? 'tablet' : ''} ${isLargeScreen ? 'large' : ''}`}>
          <div className="background-pattern" />

          <div className={`features-content ${isLargeScreen ? 'large' : ''}`}>
            <div className={`logo-container ${isLargeScreen ? 'large' : ''}`}>
              <img 
                src={athrvLogoWhite}
                alt="Athrv Cloud"
                className={`logo ${isLargeScreen ? 'large' : ''} ${isTablet ? 'tablet' : ''}`}
              />
            </div>

            <div>
              <FeatureCard
                icon={Shield}
                title="Cybersecurity Solutions"
                description="AI-powered security and embedded solutions for your infrastructure."
                isLargeScreen={isLargeScreen}
              />
              
              <FeatureCard
                  icon={DevOps}
                  title="Unified DevOps Platform"
                  description="Streamline development and operations with integrated CI/CD and automation tools."
                  isLargeScreen={isLargeScreen}
                />
              
              <FeatureCard
                icon={Settings}
                title="Seamless Integration"
                description="LLM and AWS powered platform for seamless cloud integration."
                isLargeScreen={isLargeScreen}
              />
            </div>
          </div>
        </div>
        )}

        <div className={`form-section ${isMobile || isTablet ? 'full-width' : ''} ${isLargeScreen ? 'large' : ''} ${isMobile ? 'mobile' : ''}`}>
          <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {isDarkMode ? <Moon size={12} /> : <Sun size={12} />}
          </button>

          <div className={`form-content ${isLargeScreen ? 'large' : ''}`}>
            <h1 className={`form-title ${isMobile ? 'mobile' : ''} ${isLargeScreen ? 'large' : ''}`}>
              {isOtpSent ? 'Verify Your Account' : 'Sign Up with Athrv Cloud'}
            </h1>
            
            {!isOtpSent && (
              <div className={`aws-logo-container ${isLargeScreen ? 'large' : ''}`}>
                <span className="powered-by-text">Powered by</span>
                <img
                  src={isDarkMode ? awsLogoDark : awsLogoWhite}
                  alt="AWS"
                  className={`aws-logo ${isMobile ? 'mobile' : ''} ${isLargeScreen ? 'large' : ''}`}
                />
              </div>
            )}

            {!isOtpSent ? (
              <Formik
                initialValues={{
                  userType: "individual",
                  firstName: "",
                  lastName: "",
                  email: "",
                  password: "",
                  user_phone: "",
                  organization_name: "",
                  address: "",
                  contact_email: "",
                  phone: "",
                }}
                validationSchema={signupValidationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    await handleSendOtp(values);
                  } catch (err) {
                    console.error("Form submission error:", err);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ values, handleChange, handleBlur, isSubmitting, errors, touched }) => (
                  <Form>
                    <div className={`register-type ${isLargeScreen ? 'large' : ''}`}>
                      <label className={`register-label ${isLargeScreen ? 'large' : ''}`}>
                        Register as:
                      </label>
                      <div className={`radio-group ${isLargeScreen ? 'large' : ''}`}>
                        <label className={`radio-label ${isLargeScreen ? 'large' : ''}`}>
                          <input
                            type="radio"
                            name="userType"
                            value="individual"
                            checked={userType === 'individual'}
                            onChange={(e) => setUserType(e.target.value)}
                            className={`radio-input ${isLargeScreen ? 'large' : ''}`}
                          />
                          Individual
                        </label>
                        <label className={`radio-label ${isLargeScreen ? 'large' : ''}`}>
                          <input
                            type="radio"
                            name="userType"
                            value="organization"
                            checked={userType === 'organization'}
                            onChange={(e) => setUserType(e.target.value)}
                            className={`radio-input ${isLargeScreen ? 'large' : ''}`}
                          />
                          Organisation
                        </label>
                      </div>
                    </div>

                    <Field name="firstName">
                      {({ field }) => (
                        <CustomTextField
                          label="First Name"
                          placeholder="First Name"
                          field="firstName"
                          value={values.firstName}
                          onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                          onBlur={(f) => handleBlur({ target: { name: f } })}
                          error={errors.firstName}
                          touched={touched.firstName}
                          isLargeScreen={isLargeScreen}
                        />
                      )}
                    </Field>

                    <Field name="lastName">
                      {({ field }) => (
                        <CustomTextField
                          label="Last Name"
                          placeholder="Last Name"
                          field="lastName"
                          value={values.lastName}
                          onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                          onBlur={(f) => handleBlur({ target: { name: f } })}
                          error={errors.lastName}
                          touched={touched.lastName}
                          isLargeScreen={isLargeScreen}
                        />
                      )}
                    </Field>

                    <Field name="email">
                      {({ field }) => (
                        <CustomTextField
                          label="Email Address"
                          placeholder="Email Address"
                          type="email"
                          field="email"
                          value={values.email}
                          onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                          onBlur={(f) => handleBlur({ target: { name: f } })}
                          error={errors.email}
                          touched={touched.email}
                          isLargeScreen={isLargeScreen}
                        />
                      )}
                    </Field>

                    <Field name="password">
                      {({ field }) => (
                        <CustomTextField
                          label="Password"
                          placeholder="Password"
                          type="password"
                          field="password"
                          value={values.password}
                          onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                          onBlur={(f) => handleBlur({ target: { name: f } })}
                          error={errors.password}
                          touched={touched.password}
                          isLargeScreen={isLargeScreen}
                          showPasswordToggle={true}  // Add this line
                        />
                      )}
                    </Field>

                    <Field name="user_phone">
                      {({ field }) => (
                        <CustomTextField
                          label="Phone"
                          placeholder="Phone"
                          field="user_phone"
                          value={values.user_phone}
                          onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                          onBlur={(f) => handleBlur({ target: { name: f } })}
                          error={errors.user_phone}
                          touched={touched.user_phone}
                          isLargeScreen={isLargeScreen}
                        />
                      )}
                    </Field>

                    {userType === "organization" && (
                      <>
                        <Field name="organization_name">
                          {({ field }) => (
                            <CustomTextField
                              label="Organization Name"
                              placeholder="Organization Name"
                              field="organization_name"
                              value={values.organization_name}
                              onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                              onBlur={(f) => handleBlur({ target: { name: f } })}
                              error={errors.organization_name}
                              touched={touched.organization_name}
                              isLargeScreen={isLargeScreen}
                            />
                          )}
                        </Field>

                        <Field name="address">
                          {({ field }) => (
                            <CustomTextField
                              label="Organization Address"
                              placeholder="Organization Address"
                              field="address"
                              value={values.address}
                              onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                              onBlur={(f) => handleBlur({ target: { name: f } })}
                              error={errors.address}
                              touched={touched.address}
                              isLargeScreen={isLargeScreen}
                            />
                          )}
                        </Field>

                        <Field name="contact_email">
                          {({ field }) => (
                            <CustomTextField
                              label="Organization Contact Email"
                              placeholder="Organization Contact Email"
                              type="email"
                              field="contact_email"
                              value={values.contact_email}
                              onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                              onBlur={(f) => handleBlur({ target: { name: f } })}
                              error={errors.contact_email}
                              touched={touched.contact_email}
                              isLargeScreen={isLargeScreen}
                            />
                          )}
                        </Field>

                        <Field name="phone">
                          {({ field }) => (
                            <CustomTextField
                              label="Organization Phone"
                              placeholder="Organization Phone"
                              field="phone"
                              value={values.phone}
                              onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                              onBlur={(f) => handleBlur({ target: { name: f } })}
                              error={errors.phone}
                              touched={touched.phone}
                              isLargeScreen={isLargeScreen}
                            />
                          )}
                        </Field>
                      </>
                    )}

                    {userType === "individual" && (
                      <Field name="address">
                        {({ field }) => (
                          <CustomTextField
                            label="Address"
                            placeholder="Address"
                            field="address"
                            value={values.address}
                            onChange={(f, v) => handleChange({ target: { name: f, value: v } })}
                            onBlur={(f) => handleBlur({ target: { name: f } })}
                            error={errors.address}
                            touched={touched.address}
                            isLargeScreen={isLargeScreen}
                          />
                        )}
                      </Field>
                    )}

                    <div className={`terms-container ${isLargeScreen ? 'large' : ''}`}>
                      <label className={`terms-label ${isMobile ? 'mobile' : ''} ${isLargeScreen ? 'large' : ''}`}>
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          className={`checkbox-input ${isLargeScreen ? 'large' : ''}`}
                        />
                        <span>
                          I agree with all{' '}
                          <span className="highlight terms-link" onClick={handleShowTerms}>
                            Terms and Conditions
                          </span>
                          , and{' '}
                          <span className="highlight terms-link" onClick={handleShowPrivacy}>
                            Privacy Policies
                          </span>{' '}
                          of ATHRV Cloud.
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting || !termsAccepted}
                      className={`submit-button ${(isSubmitting || !termsAccepted) ? 'disabled' : ''} ${isLargeScreen ? 'large' : ''}`}
                    >
                      {isSubmitting ? 'Loading...' : 'Sign Up'}
                    </button>

                    <p className={`login-link ${isLargeScreen ? 'large' : ''}`}>
                      Already have an account?{' '}
                      <span className="link-text" onClick={() => navigate('/login')}>
                        Log In
                      </span>
                    </p>
                  </Form>
                )}
              </Formik>
            ) : (
              <form onSubmit={handleVerifyOtp}>
                <p className={`otp-message ${isLargeScreen ? 'large' : ''}`}>
                  We've sent a verification code to <strong className="email-highlight">{savedEmail}</strong>
                </p>

                <CustomTextField
                  label="Enter OTP"
                  placeholder="Enter OTP"
                  field="otp"
                  value={otp}
                  onChange={(f, v) => setOtp(v)}
                  onBlur={() => {}}
                  error=""
                  touched={false}
                  isLargeScreen={isLargeScreen}
                />
                
                <button
                  type="submit"
                  disabled={isVerifying || !otp}
                  className={`submit-button ${(isVerifying || !otp) ? 'disabled' : ''} ${isLargeScreen ? 'large' : ''}`}
                >
                  {isVerifying ? 'Verifying...' : 'Verify OTP'}
                </button>

                <p className={`login-link ${isLargeScreen ? 'large' : ''}`}>
                  Back to{' '}
                  <span className="link-text" onClick={() => navigate('/login')}>
                    Log In
                  </span>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />

      {showTermsDialog && (
        <div className="terms-dialog-overlay" onClick={handleCloseTermsDialog}>
          <div className="terms-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="terms-dialog-header">
              <h2>{dialogContent.title}</h2>
              <button className="terms-dialog-close" onClick={handleCloseTermsDialog}>
                ×
              </button>
            </div>
            <div className="terms-dialog-content">
              {dialogContent.content}
            </div>
            <div className="terms-dialog-footer">
              <button className="terms-dialog-button" onClick={handleCloseTermsDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUpPage;